import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Lightweight interest-capture modal.
 * Reuses the /api/waitlist endpoint with different `source` values so all
 * expressions of interest flow into a single admin inbox.
 *
 * Props:
 *  - open, onClose
 *  - source: "four-levels" | "weekly-edge" | "daily-edge" | "pattern-reveal"
 *  - title: display heading, e.g. "The Four Levels Experience"
 *  - lede: one calm sentence under the heading
 *  - submitLabel: button label ("Keep me informed", "Begin the conversation")
 *  - afterAction: optional { label, href } shown after success (e.g. open FourLevelsExperience.com)
 */
export default function InterestModal({
  open,
  onClose,
  source,
  title,
  lede,
  submitLabel = "Send",
  afterAction = null,
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setEmail("");
      setNote("");
      setDone(false);
      setLoading(false);
      const onKey = (e) => e.key === "Escape" && onClose();
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = "";
      };
    }
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/waitlist", {
        email,
        name: name || null,
        source,
        note: note || null,
      });
      setDone(true);
      toast.success("Your interest has been received. Thank you.");
    } catch {
      toast.error("We couldn't reach the server just now — please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-ink/40 backdrop-blur-sm animate-gentle-fade"
      onClick={onClose}
      data-testid={`interest-modal-${source}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`interest-modal-title-${source}`}
    >
      <div
        className="w-full max-w-lg rounded-lg bg-brand-cream border border-brand-rule/70 p-8 sm:p-10 shadow-[0_30px_80px_-20px_rgba(26,37,64,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-6 mb-6">
          <div>
            <div className="eyebrow mb-2">Express interest</div>
            <h2
              id={`interest-modal-title-${source}`}
              className="font-serif text-[26px] sm:text-[30px] text-brand-ink leading-tight"
            >
              {title}
            </h2>
            {lede && !done && (
              <p className="mt-3 text-[14.5px] leading-relaxed text-brand-ink/70">
                {lede}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid={`interest-modal-close-${source}`}
            className="text-brand-ink/50 hover:text-brand-ink"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {done ? (
          <div data-testid={`interest-modal-success-${source}`}>
            <p className="font-serif text-[22px] text-brand-ink">Thank you.</p>
            <p className="mt-3 text-[15px] leading-relaxed text-brand-ink/70">
              Your interest has been received. Dan will personally follow up
              soon. In the meantime, take your time — explore what resonates.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              {afterAction && (
                <a
                  href={afterAction.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`interest-modal-after-${source}`}
                  className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-2.5 text-brand-cream text-[13.5px] hover:bg-brand-blue transition-colors"
                >
                  {afterAction.label} <span aria-hidden>↗</span>
                </a>
              )}
              <button
                onClick={onClose}
                className="text-[13.5px] text-brand-ink/70 hover:text-brand-blue"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-[11.5px] text-brand-ink/60 mb-1.5 uppercase tracking-wider">
                Name (optional)
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid={`interest-name-${source}`}
                className="w-full rounded-md border border-brand-rule bg-white/70 px-4 py-2.5 text-[15px] focus:outline-none focus:border-brand-blue"
              />
            </div>
            <div>
              <label className="block text-[11.5px] text-brand-ink/60 mb-1.5 uppercase tracking-wider">
                Email
              </label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={`interest-email-${source}`}
                className="w-full rounded-md border border-brand-rule bg-white/70 px-4 py-2.5 text-[15px] focus:outline-none focus:border-brand-blue"
              />
            </div>
            <div>
              <label className="block text-[11.5px] text-brand-ink/60 mb-1.5 uppercase tracking-wider">
                Anything you'd like Dan to know? (optional)
              </label>
              <textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid={`interest-note-${source}`}
                placeholder="A sentence or two is more than enough."
                className="w-full rounded-md border border-brand-rule bg-white/70 px-4 py-2.5 text-[15px] resize-y focus:outline-none focus:border-brand-blue"
              />
            </div>
            <div className="flex items-center justify-between pt-2">
              <p className="text-[12px] text-brand-ink/50">
                No marketing. A personal reply.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid={`interest-submit-${source}`}
                className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-2.5 text-brand-cream text-[13.5px] hover:bg-brand-blue transition-colors disabled:opacity-60"
              >
                {loading ? "Sending…" : submitLabel} <span aria-hidden>→</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
